class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent))

    const newNodeWrapper = document.createElement("div")
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
    const newNode = newNodeWrapper.firstChild

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        oldNode.parentNode.replaceChild(newNode, oldNode)
        postProcessCallbacks?.forEach((callback) => callback(newNode))
      })
    } else {
      // Fallback: double-buffer approach
      const uniqueKey = Date.now()
      oldNode.querySelectorAll("[id], [form]").forEach((element) => {
        element.id && (element.id = `${element.id}-${uniqueKey}`)
        element.form &&
          element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`)
      })

      oldNode.parentNode.insertBefore(newNode, oldNode)
      oldNode.style.display = "none"

      postProcessCallbacks?.forEach((callback) => callback(newNode))

      setTimeout(() => oldNode.remove(), 500)
    }
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script")
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      })
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
    })
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super()
    this.input = this.querySelector("input")
    this.changeEvent = new Event("change", { bubbles: true })
    this.input.addEventListener("change", this.onInputChange.bind(this))
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    )
  }

  quantityUpdateUnsubscriber = undefined

  connectedCallback() {
    this.validateQtyRules()
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    )
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber()
    }
  }

  onInputChange(event) {
    this.validateQtyRules()
  }

  onButtonClick(event) {
    event.preventDefault()
    const previousValue = this.input.value

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min
      } else {
        this.input.stepUp()
      }
    } else {
      this.input.stepDown()
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min)
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value)
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']")
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min))
    }
    if (this.input.max) {
      const max = parseInt(this.input.max)
      const buttonPlus = this.querySelector(".quantity__button[name='plus']")
      buttonPlus.classList.toggle("disabled", value >= max)
    }
  }
}

customElements.define("quantity-input-dawn", QuantityInputDawn)

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target)
      this.updateSelectionMetadata(event)

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues
        }
      })
    })
  }

  updateSelectionMetadata({ target }) {
    if (!target) return
    const { value, tagName } = target

    if (tagName === "SELECT" && target.selectedOptions?.length) {
      const previousSelected = Array.from(target.options).find((option) => option.getAttribute("selected"))
      if (previousSelected) previousSelected.removeAttribute("selected")

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value)
      if (newSelectedOption) {
        newSelectedOption.setAttribute("selected", "selected")
      }

      const selectedOption = target.selectedOptions[0]
      if (!selectedOption) return

      const swatchValue = selectedOption.dataset.optionSwatchValue
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        ?.querySelector("[data-selected-value] > .product-option__swatch")
      if (!selectedDropdownSwatchValue) return
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue)
        selectedDropdownSwatchValue.classList.remove("product-option__swatch--unavailable")
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset")
        selectedDropdownSwatchValue.classList.add("product-option__swatch--unavailable")
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        selectedOption.dataset.optionSwatchFocalPoint || "unset"
      )
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]")
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
    }
  }

  getInputForEventTarget(target) {
    if (target.tagName === "SELECT") {
      return target.selectedOptions[0] || target
    }
    return target
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId
    )
  }
}
customElements.define("product-variant-selects", ProductVariantSelects)

// Edited for STAU-575
function isGroupSelected(inputs) {
  return [...inputs].some((input) => input.checked)
}
function isSelectGroupSelected(selects) {
  return [...selects].some((select) => {
    const selected = select.selectedOptions[0]
    return selected && selected.value !== "" && !selected.hidden
  })
}
function isSizeOK() {
  const sizeInputs = [...document.querySelectorAll('.product--variant-size input[type="radio"]')]
  const sizeSelects = [...document.querySelectorAll('.product--variant-size select')]
  const hasSize = sizeInputs.length > 0 || sizeSelects.length > 0
  return (
    !hasSize ||
    isGroupSelected(sizeInputs) ||
    isSelectGroupSelected(sizeSelects)
  )
}
function isInseamOK() {
  const inseamInputs = [...document.querySelectorAll('.product--variant-inseam input[type="radio"]')]
  const inseamSelects = [...document.querySelectorAll('.product--variant-inseam select')]
  const hasInseam = inseamInputs.length > 0 || inseamSelects.length > 0
  return (
    !hasInseam ||
    isGroupSelected(inseamInputs) ||
    isSelectGroupSelected(inseamSelects)
  )
}

function computeOOSState(selectedVariant, productInfoNode = document) {
  const productInfo =
    productInfoNode instanceof HTMLElement
      ? productInfoNode.closest('product-info') || productInfoNode
      : document.querySelector('product-info');

  if (!productInfo) return false;
  const allVariants = productInfo.getAllVariants?.();

  if (allVariants?.length && !allVariants.some((variant) => variant.available)) {
    return true;
  }

  const hasSize =
    !!productInfo.querySelector('.product--variant-size');

  const hasInseam =
    !!productInfo.querySelector('.product--variant-inseam');

  // Color-only products
  if (!hasSize && !hasInseam) {
    const variant =
      selectedVariant ||
      productInfo.getSelectedVariant?.(productInfo);
    return variant ? !variant.available : false;
  }

  const sizeSelected = isSizeOK();
  const inseamSelected = isInseamOK();

  if (!sizeSelected) return false;

  if (inseamSelected) {
    return selectedVariant ? !selectedVariant.available : false;
  }

  const sizeGroup = productInfo.querySelector('.product--variant-size');
  const selectedSize =
    sizeGroup?.querySelector('input[type="radio"]:checked')?.value ||
    sizeGroup?.querySelector('select')?.selectedOptions[0]?.value ||
    null;

  if (!selectedSize) return false;

  if (!allVariants?.length) return false;

  const optionGroups = Array.from(
    productInfo.querySelectorAll('.product-form__input, fieldset')
  );

  const sizeIndex = optionGroups.findIndex((group) =>
    group.matches('.product--variant-size')
  );

  if (sizeIndex === -1) return false;

  const hasAvailableVariantForSize = allVariants.some(
    (variant) =>
      variant.available &&
      Array.isArray(variant.options) &&
      variant.options[sizeIndex] === selectedSize
  );

  return !hasAvailableVariantForSize;
}

function openBisModal(e, preselect = true) {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  const modal = document.getElementById("bis-modal");
  if (!modal) return;

  initBisModal();

  // Reset modal to the initial form state
  modal.querySelector(".bis-modal__form").hidden = false;
  modal.querySelector(".bis-modal__success").hidden = true;

  resetBisModalState();

  modal.removeAttribute("hidden");
  modal
    .querySelector(".bis-modal__overlay")
    ?.classList.add("is-open");

  document.body.classList.add("overflow-hidden");

  initBisVariantPicker();

  // Ensure BIS handler is attached (critical for custom CTA products)
  const notifyBtn = document.querySelector("#back-in-stock-form .notify_button");
  if (notifyBtn && !notifyBtn.dataset.bisBound) {
    const productForm = document.querySelector("product-form");
    if (productForm && typeof productForm.initBISHandler === "function") {
      productForm.initBISHandler();
    }
  }

  if (preselect) {
    syncBisSelections();
  } else {
    resetBisSelections();
  }
}

function closeBisModal() {

  const modal = document.getElementById("bis-modal");

  modal
    .querySelector(".bis-modal__overlay")
    ?.classList.remove("is-open");

  modal.setAttribute("hidden", "");

  document.body.classList.remove("overflow-hidden");

  // Clear size/inseam selection made when opening BIS
  document
    .querySelectorAll(
      ".product--variant-size select, .product--variant-inseam select"
    )
    .forEach((select) => {
      select.value = "";
      select.dispatchEvent(new Event("change", { bubbles: true }));
    });
}

function initBisModal() {
  const modal = document.getElementById("bis-modal");
  if (!modal) return;

  const closeBtn = modal.querySelector(".bis-modal__close");
  const overlay = modal.querySelector(".bis-modal__overlay");
  const successBtn = modal.querySelector(".bis-success-btn");
  const errorBtn = modal.querySelector(".bis-error-btn");

  if (closeBtn && !closeBtn.dataset.bound) {
    closeBtn.addEventListener("click", closeBisModal);
    closeBtn.dataset.bound = "true";
  }

  if (overlay && !overlay.dataset.bound) {
    overlay.addEventListener("click", closeBisModal);
    overlay.dataset.bound = "true";
  }

  if (successBtn && !successBtn.dataset.bound) {
    successBtn.addEventListener("click", closeBisModal);
    successBtn.dataset.bound = "true";
  }

  if (errorBtn && !errorBtn.dataset.bound) {
    errorBtn.addEventListener("click", closeBisModal);
    errorBtn.dataset.bound = "true";
  }
}

function initBisVariantPicker() {
  const variantsJson = document.getElementById("bis-variants-json");
  if (!variantsJson) return;

  const variants = JSON.parse(variantsJson.textContent);
  const selects = document.querySelectorAll("#bis-modal .bis-option-select");

  if (!selects.length) return;

  // Prevent binding twice
  selects.forEach((select) => {
    if (select.dataset.bound) return;
    select.dataset.bound = "true";

    select.addEventListener("change", () => {
      updateBisOptions();
    });
  });

  updateBisOptions();

  function updateBisOptions() {
    const selectedValues = Array.from(selects).map((s) => s.value);

    // Update hidden variant id
    const matchedVariant = variants.find((variant) =>
      selectedValues.every((value, index) => !value || variant.options[index + 1] === value)
    );

    if (matchedVariant) {
      document.getElementById("bis-selected-variant-id").value = matchedVariant.id;
    }

    // Enable/disable options
    selects.forEach((select, selectIndex) => {
      Array.from(select.options).forEach((option) => {
        if (!option.value) return;

        const enabled = variants.some((variant) => {
          if (variant.available) return false;

          return selectedValues.every((selectedValue, idx) => {
            if (idx === selectIndex) {
              return variant.options[idx + 1] === option.value;
            }

            return !selectedValue || variant.options[idx + 1] === selectedValue;
          });
        });

        option.disabled = !enabled;
      });
    });
  }
}

function syncBisSelections() {
  document.querySelectorAll("dropdown-input select").forEach((pageSelect) => {
    const name = pageSelect.name.toLowerCase();

    let optionName = null;

    if (name.includes("color")) optionName = "color";
    else if (name.includes("size")) optionName = "size";
    else if (name.includes("inseam")) optionName = "inseam";

    if (!optionName) return;

    const popupSelect = document.querySelector(
      `#bis-modal .bis-option-select[data-option-name="${optionName}"]`
    );

    if (!popupSelect) return;

    popupSelect.value = pageSelect.value;
    popupSelect.dispatchEvent(new Event("change", { bubbles: true }));
  });
}
function resetBisSelections() {
  document.querySelectorAll("#bis-modal .bis-option-select").forEach((select) => {
    select.value = "";
    select.dispatchEvent(new Event("change", { bubbles: true }));
  });
}
function showBisSuccess() {
  document.querySelector(".bis-modal__form").hidden = true;
  document.querySelector(".bis-modal__error-popup").hidden = true;
  document.querySelector(".bis-modal__success").hidden = false;
}
function showBisError() {
  const form = document.querySelector(".bis-modal__form");
  const success = document.querySelector(".bis-modal__success");
  const error = document.querySelector(".bis-modal__error-popup");

  if (form) {
    form.hidden = true;
  }

  if (success) {
    success.hidden = true;
  }

  if (error) {
    error.hidden = false;
  }
}
function resetBisModalState() {
  document.querySelector(".bis-modal__form").hidden = false;
  document.querySelector(".bis-modal__success").hidden = true;
  document.querySelector(".bis-modal__error-popup").hidden = true;
}

if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined
      quantityForm = undefined
      onVariantChangeUnsubscriber = undefined
      cartUpdateUnsubscriber = undefined
      abortController = undefined
      pendingRequestUrl = null
      preProcessHtmlCallbacks = []
      postProcessHtmlCallbacks = []

      constructor() {
        super()

        this.isUpdatingVariant = false;

        this.quantityInput = this.querySelector(".quantity__input")
        this.productMediaGallery = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        )
        this.productVariantSelects = this.querySelector("product-variant-selects")
      }

      connectedCallback() {
        this.initializeProductSwapUtility()

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        )

        this.onOOSOptionSelected = (event) => {
          openBisModal(null, true);
          syncBisSelections();
        };

        document.addEventListener("option:oos", this.onOOSOptionSelected);

        this.initQuantityHandlers()
        this.dispatchEvent(new CustomEvent("product-info:loaded", { bubbles: true }))
        this.initUpdateMedia()

        if (
          this.productMediaGallery &&
          this.productMediaGallery.dataset.hideOtherVariantsMedia === "true"
        ) {
          this.updateVariantMedia()
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback)
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return

        this.quantityForm = this.querySelector(".product-form__quantity")
        if (!this.quantityForm) return

        this.setQuantityBoundries()
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          )
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl =
          parseInt(
            window.location.search
              .split("?")
              .filter((item) => item.includes("variant="))[0]
              ?.split("=")[1]
          ) || null
        if (!variantIdFromUrl) {
          return
        }
        const variant = this.getSelectedVariant(this)
        if (!variant) {
          return
        }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id)
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber()
        this.cartUpdateUnsubscriber?.()
        if (this.onOOSOptionSelected) {
          document.removeEventListener("option:oos", this.onOOSOptionSelected);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) => element.classList.add("scroll-trigger--cancel"))
        )
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init()
          window?.ProductModel?.loadShopifyXR()
        })
      }

      normalizeProductUrl(url) {
        if (!url) return url

        try {
          const parsedUrl = new URL(url, window.location.origin)
          parsedUrl.searchParams.delete("variant")
          return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
        } catch (e) {
          return url.replace(/([?&])variant=[^&]*(?:&?)/, (match, sep, tail) => {
            if (tail === "&") return sep
            return ""
          }).replace(/\?$/, "")
        }
      }

      getVariantIdFromUrl(url) {
        if (!url) return null

        try {
          const parsedUrl = new URL(url, window.location.origin)
          return parsedUrl.searchParams.get("variant")
        } catch (e) {
          const match = url.match(/(?:[?&])variant=(\d+)/)
          return match ? match[1] : null
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return

        const isColorChange = !!target.closest(".product--variant-color");

        this.resetProductFormState()
        const rawProductUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
        const productUrl = this.normalizeProductUrl(rawProductUrl)
        this.pendingRequestUrl = productUrl
        const shouldSwapProduct = this.dataset.url !== productUrl
        const shouldFetchFullPage = this.dataset.updateUrl === "true" && shouldSwapProduct

        // Get the current variant ID immediately from the selected options
        let currentVariantId = this.getCurrentVariantId(selectedOptionValues)
        if (!currentVariantId) {
          currentVariantId = this.getVariantIdFromUrl(rawProductUrl)
        }

        // Update selected variant inputs immediately before fetch so add-to-cart uses the right variant
        const allVariants = this.getAllVariants();
        const selectedVariant = currentVariantId
          ? allVariants?.find((variant) => String(variant.id) === String(currentVariantId)) || null
          : null;

        if (currentVariantId) {
          this.updateVariantInputs(currentVariantId);
          this.updateURL(productUrl, currentVariantId);
        }

        this.updateBISVisibility(selectedVariant);
        const productForm = this.querySelector("product-form");

        if (productForm) {
          const isOOS = computeOOSState(selectedVariant, this);
          if (isOOS) {
            openBisModal(evt);
          }
        }

        this.isColorChange = isColorChange;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        })

        if (typeof this.initBISHandler === "function") {
          this.initBISHandler();
        }
      }

      resetProductFormState() {
        const productForm = this.productForm
        productForm?.handleErrorMessage()
      }

      updateBISVisibility(selectedVariant) {
        const notifyFormWrapper = document.querySelector(".back-in-stock_wrapper");

        if (!notifyFormWrapper) return;

        const showBIS = computeOOSState(selectedVariant, this);

        notifyFormWrapper.style.display = showBIS ? "block" : "none";
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove()

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : "product-info"
          const variant = this.getSelectedVariant(html.querySelector(selector))
          this.updateURL(productUrl, variant?.id)

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            )
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            )
          }
        }
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.isUpdatingVariant = true;
        this.abortController?.abort()
        this.abortController = new AbortController()

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null
            const html = new DOMParser().parseFromString(responseText, "text/html")
            callback(html)
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus()
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          })
          .finally(() => {
            // Always clear the updating flag when fetch completes or errors
            this.isUpdatingVariant = false;
          })
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML
        return !!selectedVariant ? JSON.parse(selectedVariant) : null
      }

      getAllVariants() {
        if (this.allVariants) return this.allVariants

        const variantsJson = this.querySelector("script[data-all-variants]")?.textContent
        if (!variantsJson) return null

        try {
          this.allVariants = JSON.parse(variantsJson)
          return this.allVariants
        } catch (error) {
          return null
        }
      }

      getSelectedOptionValuesByGroup() {
        if (!this.variantSelectors) return []

        const optionGroups = Array.from(
          this.variantSelectors.querySelectorAll(".product-form__input, fieldset")
        )

        return optionGroups.map((group) => {
          const select = group.querySelector("select")
          if (select) {
            return select.selectedOptions[0]?.value || null
          }
          const checkedInput = group.querySelector("input[type='radio']:checked")
          return checkedInput ? checkedInput.value : null
        })
      }

      isOptionValueAvailable(optionGroup, optionValue) {
        const allVariants = this.getAllVariants()
        if (!allVariants || !this.variantSelectors || !optionGroup) return true

        const optionGroups = Array.from(
          this.variantSelectors.querySelectorAll(".product-form__input, fieldset")
        )
        const groupIndex = optionGroups.indexOf(optionGroup)
        if (groupIndex === -1) return true

        const selections = this.getSelectedOptionValuesByGroup()
        selections[groupIndex] = optionValue

        // Only mark options unavailable once all preceding groups are selected.
        const hasSelectionInPreviousGroups = selections
          .slice(0, groupIndex)
          .some((value) => value != null)
        if (!hasSelectionInPreviousGroups && groupIndex > 0) return true

        return allVariants.some((variant) =>
          variant.options.every((option, index) => {
            if (selections[index] == null) return true
            return option === selections[index]
          }) && variant.available
        )
      }

      getCurrentVariantId(selectedOptionValues = []) {
        const allVariants = this.getAllVariants()
        const variantSelects = this.querySelector("product-variant-selects")
        const groupedOptionValues = Array.from(
          variantSelects?.querySelectorAll(":scope > .product-form__input, :scope > fieldset") || []
        ).map((group) => {
          const select = group.querySelector("select")
          if (select) {
            return select.selectedOptions[0]
          }

          return group.querySelector("input:checked")
        })

        const selectedOptionLabels = groupedOptionValues
          .filter((option) => option && option.value !== "" && !option.hidden)
          .map((option) => option.value)

        const totalOptionCount = allVariants?.[0]?.options?.length || 0
        if (allVariants && selectedOptionLabels.length === totalOptionCount && totalOptionCount > 0) {
          const matchedVariant = allVariants.find(
            (variant) =>
              Array.isArray(variant.options) &&
              variant.options.length === totalOptionCount &&
              variant.options.every((option, index) => option === selectedOptionLabels[index])
          )

          if (matchedVariant) {
            return matchedVariant.id
          }
        }

        if (!allVariants || selectedOptionLabels.length === 0) {
          const currentVariantData = this.querySelector(
            "product-variant-selects [data-selected-variant]"
          )?.innerHTML
          if (currentVariantData) {
            try {
              const variant = JSON.parse(currentVariantData)
              return variant?.id
            } catch (e) {
              return null
            }
          }
        }

        return null
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        try {
          const parsedUrl = new URL(url, window.location.origin)
          if (!shouldFetchFullPage) {
            parsedUrl.searchParams.set("section_id", this.sectionId)
          }
          if (optionValues.length) {
            parsedUrl.searchParams.set("option_values", optionValues.join(","))
          }
          return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
        } catch (e) {
          const params = []
          !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)
          if (optionValues.length) {
            params.push(`option_values=${optionValues.join(",")}`)
          }
          return `${url}?${params.join("&")}`
        }
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects")

        if (productVariantSelects) {
          // Save current selections BEFORE DOM update
          const inseamInputs = [...document.querySelectorAll('.product--variant-inseam input[type="radio"]')]
          const sizeInputs = [...document.querySelectorAll('.product--variant-size input[type="radio"]')]
          const selectedSize =
            sizeInputs.find((input) => input.checked)?.value ||
            document.querySelector('.product--variant-size select')?.value ||
            null;

          const selectedInseam =
            inseamInputs.find((input) => input.checked)?.value ||
            document.querySelector('.product--variant-inseam select')?.value ||
            null;

          // Preserve current checked state in new HTML before DOM swap
          const currentChecked = this.variantSelectors.querySelectorAll('fieldset input:checked')
          currentChecked.forEach(input => {
            const id = input.dataset.optionValueId
            if (id) {
              const match = productVariantSelects.querySelector(`input[data-option-value-id="${id}"]`)
              if (match) match.setAttribute('checked', '')
            }
          })

          // Preserve current dropdown select state in new HTML before DOM swap
          const currentSelectValues = Array.from(
            this.variantSelectors.querySelectorAll('select')
          ).map((select) => ({
            name: select.name,
            value: select.value
          }))

          currentSelectValues.forEach(({ name, value }) => {
            const newSelect =
              productVariantSelects.querySelector(`select[name="${name}"]`);

            if (!newSelect) return;

            Array.from(newSelect.options).forEach((option) => {
              option.selected = option.value === value;

              if (option.value === value) {
                option.setAttribute("selected", "selected");
              } else {
                option.removeAttribute("selected");
              }
            });

            newSelect.value = value;

          });

          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks,
            [
              (newNode) => {
                newNode.querySelectorAll('dropdown-input').forEach((dropdown) => {
                  dropdown.update?.()
                })
              }
            ]
          );
          // Wait for DOM update to complete, then restore selections and remove error states
          const inseamSelects = [...document.querySelectorAll('.product--variant-inseam select')]
          if (inseamInputs.length > 0 || inseamSelects.length > 0) {
            const backInStock = document.querySelector(".back-in-stock_wrapper.BIS_wrapper")

            const updateAvailabilityForGroup = (groupSelector) => {
              document.querySelectorAll(groupSelector).forEach((group) => {
                const optionGroup = group.closest(".product-form__input, fieldset")

                group.querySelectorAll("input[type='radio']").forEach((input) => {
                  let available = true;

                  // Don't show Inseam OOS on page load
                  if (groupSelector === '.product--variant-inseam') {
                    const sizeSelect = document.querySelector('.product--variant-size select');
                    const sizeRadio = document.querySelector('.product--variant-size input:checked');

                    const sizeSelected =
                      sizeRadio ||
                      (sizeSelect && sizeSelect.value !== '');

                    if (!sizeSelected) {
                      input.removeAttribute('aria-disabled');
                      input.classList.remove('disabled');

                      const label = input.nextElementSibling;
                      const labelValue = label?.querySelector('span') || label;

                      if (labelValue) {
                        labelValue.textContent =
                          input.dataset.optionLabel || input.value;

                        labelValue.classList.remove('option-unavailable');
                      }

                      return;
                    }
                  }

                  available = this.isOptionValueAvailable(
                    optionGroup,
                    input.value
                  );

                  const optionLabel =
                    input.dataset.optionLabel || input.value;

                  const label = input.nextElementSibling;
                  const labelValue =
                    label?.querySelector('span') || label;
                  const isSelected = input.checked || input.selected;
                  if (!available) {
                    input.setAttribute('aria-disabled', 'true');
                    input.classList.add('disabled');
                    const outOfStockText = "Out of Stock";      
                    if (!isSelected) {
                      labelValue.innerHTML = `
                        <span class="option-left">
                          ${optionLabel} - ${outOfStockText}
                        </span>
                        <span class="option-right">
                          ${notifyText}
                        </span>
                      `;
                      labelValue.classList.add('option-unavailable');
                    }
                  } else {
                    input.removeAttribute('aria-disabled');
                    input.classList.remove('disabled');

                    if (labelValue) {
                      labelValue.textContent = optionLabel;
                      labelValue.classList.remove('option-unavailable');
                    }
                  }
                });

                const select = group.querySelector("select");

                if (select) {
                  Array.from(select.options).forEach((option) => {
                    if (option.value === "" || option.hidden) return;

                    // Don't show Inseam OOS until Size is selected
                    if (groupSelector === ".product--variant-inseam") {
                      const sizeSelect = document.querySelector(
                        ".product--variant-size select"
                      );

                      const sizeRadio = document.querySelector(
                        ".product--variant-size input:checked"
                      );

                      const sizeSelected =
                        sizeRadio?.value ||
                        sizeSelect?.value ||
                        "";

                      if (!sizeSelected) {
                        option.dataset.unavailable = "false";
                        return;
                      }
                    }

                    const available = this.isOptionValueAvailable(
                      optionGroup,
                      option.value
                    );

                    option.dataset.unavailable = available
                      ? "false"
                      : "true";
                  });
                }
              })
            }

            // Update availability for both dimensions based on the current selection context
            requestIdleCallback(() => {
              updateAvailabilityForGroup('.product--variant-inseam');
              updateAvailabilityForGroup('.product--variant-size');

              this.querySelectorAll('dropdown-input').forEach((dropdown) => {
                dropdown.update();
              });
            });
          }
        }
      }

      // prevent auto-selection of other variant group

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html)
          this.updateOptionValues(html)

          this.updateVariantInputs(variant?.id)
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price)
          this.updateVariantDetails(variant?.id)

          if (
            this.productMediaGallery &&
            this.productMediaGallery.dataset.hideOtherVariantsMedia === "true"
          ) {
            this.updateVariantMedia(html)
          }

          if (!variant) {
            this.setUnavailable()
            return
          }

          if (this.isColorChange) {
            this.updateMedia(variant?.featured_media?.id);
          }
          this.isColorChange = false;

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`)
            const destination = this.querySelector(`#${id}-${this.dataset.section}`)

            if (source && destination) {
              destination.innerHTML = source.innerHTML

              if (id === "Inventory") {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold)
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity)
                destination.classList.toggle(
                  "hidden",
                  (inventoryThreshold && inventoryQuantity > inventoryThreshold) ||
                  inventoryQuantity < 1
                )
              } else {
                destination.classList.toggle("hidden", shouldHide(source))
              }
            }
          }

          updateSourceFromDestination("ProductPurchaseInfo")
          updateSourceFromDestination("ProductStickyCart")

          updateSourceFromDestination("price")
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"))

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "")

          this.updateQuantityRules(this.sectionId, html)
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove("hidden")

          const isDisabled =
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true;

          this.productForm?.toggleSubmitButton(isDisabled);

          const productInfo = this.productForm?.closest("product-info");

          if (productInfo) {
            const isOOS = computeOOSState(
              variant,
              productInfo
            );

            if (isOOS) {
              this.productForm.submitButtonText.textContent = "Join the Waitlist";
              this.productForm.submitButton.disabled = false;
              this.productForm.submitButton.removeAttribute("disabled");
              this.productForm.submitButton.classList.remove("disabled");
            } else {
              this.productForm.submitButtonText.textContent =
                document.body.classList.contains("template--product") ? "Add to Bag" : (window.variantStrings?.addToCart || "Add to Cart");

              this.productForm.submitButton.disabled = false;
              this.productForm.submitButton.removeAttribute("disabled");
              this.productForm.submitButton.classList.remove("disabled");
            }
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          })
        }
        const modal = document.getElementById("bis-modal");

        if (modal && !modal.hidden) {
            syncBisSelections();
        }
      }

      updateVariantMedia(html) {
        const select_variant_options_handle = this.querySelector(
          "product-variant-selects [data-selected-variant-options-handle]"
        )?.innerHTML.trim()

        if (!select_variant_options_handle) return

        this.productMediaGallery
          .querySelectorAll("[data-media-group]")
          .forEach((element) => element.classList.add("hidden"))

        this.productMediaGallery
          .querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`)
          .forEach((element) => element.classList.remove("hidden"))

        this.productMediaGallery
          .querySelectorAll("swiper-product-gallery")
          .forEach((swiperSlide) => {
            if (swiperSlide.instance) {
              swiperSlide.instance.update()
            } else {
              swiperSlide.init()
            }
          })
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]')
          input.value = variantId ?? ""
          input.dispatchEvent(new Event("change", { bubbles: true }))
        })
      }

      updateURL(url, variantId) {
        try {
          const parsedUrl = new URL(url, window.location.origin)
          if (variantId) {
            parsedUrl.searchParams.set("variant", variantId)
          } else {
            parsedUrl.searchParams.delete("variant")
          }
          const newUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`

          this.querySelector("share-button")?.updateUrl(`${window.shopUrl}${newUrl}`)
          if (this.dataset.updateUrl === "false") return
          window.history.replaceState({}, "", newUrl)
        } catch (e) {
          const newUrl = `${url}${variantId ? `?variant=${variantId}` : ""}`
          this.querySelector("share-button")?.updateUrl(`${window.shopUrl}${newUrl}`)
          if (this.dataset.updateUrl === "false") return
          window.history.replaceState({}, "", newUrl)
        }
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return
        if (!sellingPlanId) {
          window.history.replaceState(
            {},
            "",
            `${window.location.href.replace(/&selling_plan=\d+/, "")}`
          )
        } else {
          const params = new URLSearchParams(window.location.search)
          params[params.has("variant") ? "set" : "append"]("variant", selectedVariantId)
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"]("selling_plan", sellingPlanId)
          } else {
            params.delete("selling_plan")
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`)
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll(".product__metafield_for_variant-block")
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute("data-block-id")
            const metafieldDataElement = this.querySelector(
              `#product__metafield_for_variant-block--${block_id}`
            )
            if (!metafieldDataElement) {
              return
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent)
              productVariantDetail.innerHTML =
                productVariantDetailsData?.[variantId]?.replaceAll("\n", "<br />") || ""
            } catch (error) {
              console.error("Error parsing JSON", error)
            }
          })
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount")
        if (!discountBadge) return

        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden")
          const percentageElement = discountBadge.querySelector(".percentage")
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          )
          // update discount percentage
          percentageElement.textContent = discountPercentage
        } else {
          discountBadge.classList.add("hidden")
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable)

        const selectors = ["price", "Inventory", "Sku", "Quantity-Rules"]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ")
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add("hidden"))
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return

        if (
          this.querySelector(".main-product__media")?.hasAttribute("data-desktop-grid") &&
          window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId)
        }

        const productMedia = this.querySelector("swiper-product-gallery")
        productMedia?.setActiveMedia(variantFeaturedMediaId)
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach((img) => {
          if (img.getAttribute("data-media-id") == variantFeaturedMediaId) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            })
          }
        })
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1
        }

        let min = data.min
        const max = data.max === null ? data.max : data.max - data.cartQuantity
        if (max !== null) min = Math.min(min, max)
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

        this.quantityInput.min = min

        if (max) {
          this.quantityInput.max = max
        } else {
          this.quantityInput.removeAttribute("max")
        }
        this.quantityInput.value = min

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined)
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value
        if (!currentVariantId) return

        this.querySelector(".quantity__rules-cart .loading__spinner").classList.remove("hidden")
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html")
            this.updateQuantityRules(this.dataset.section, html)
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(".quantity__rules-cart .loading__spinner").classList.add("hidden")
          )
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return
        this.setQuantityBoundries()

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`)
        const selectors = [".quantity__input", ".quantity__rules", ".quantity__label"]
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector)
          const updated = quantityFormUpdated.querySelector(selector)
          if (!current || !updated) continue
          if (selector === ".quantity__input") {
            const attributes = ["data-cart-quantity", "data-min", "data-max", "step"]
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute)
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated)
              } else {
                current.removeAttribute(attribute)
              }
            }
          } else {
            current.innerHTML = updated.innerHTML
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`)
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`)
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`)
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects")
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        )
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        )
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        )
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`)
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section
      }
    }
  )
}

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super()

        this.form = this.querySelector("form")
        this.variantIdInput.disabled = false

        this.form.addEventListener("submit", this.onSubmitHandler.bind(this))
        this.cart =
          document.querySelector("cart-notification") || document.querySelector("cart-drawer")
        this.submitButton = this.querySelector('[type="submit"]')
        this.submitButtonText = this.submitButton.querySelector("span")

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog")

        this.hideErrors = this.dataset.hideErrors === "true"

        this.initBISHandler()
        this.initPageLoadState()

        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        this.currentVariant = data.variant;
        const productInfo = this.closest("product-info");
          if (!productInfo) return;

        const isOOS = computeOOSState(this.currentVariant, productInfo);
        const bisWrapper = document.querySelector(
          ".back-in-stock_wrapper.BIS_wrapper"
        );

        if (isOOS) {
          this.submitButtonText.textContent = "Join the Waitlist";
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute("disabled");
          this.submitButton.classList.remove("disabled");
        } else {
          this.submitButtonText.textContent =
            document.body.classList.contains("template--product") ? "Add to Bag" : (window.variantStrings?.addToCart || "Add to Cart");

          this.submitButton.disabled = false;
          this.submitButton.removeAttribute("disabled");
          this.submitButton.classList.remove("disabled");
        }

        // BIS visibility
        if (bisWrapper) {
          bisWrapper.style.display = isOOS ? "block" : "none";
        }
      });
      }

      initBISHandler() {
        // Use a more robust approach that works even if modal loads later
        const attachBISListener = () => {
          const notifyBtn = document.querySelector("#back-in-stock-form .notify_button")
          if (!notifyBtn) return false
          if (notifyBtn.dataset.bisBound) return true
          
          notifyBtn.dataset.bisBound = "true"
          notifyBtn.addEventListener("click", (e) => {
            e.preventDefault()
            // Validate all required dropdowns first
            const selects = document.querySelectorAll(".bis-option-select");

            for (const select of selects) {
              if (!select.value) {
                select.reportValidity();
                return;
              }
            }
            // Validate email
            const emailInput = document.getElementById("notify_email")
            if (!emailInput || !emailInput.checkValidity()) {
              emailInput?.reportValidity()
              return
            }
            const email = emailInput.value
            const productId = document.getElementById("hidden_product_id")?.value
            const variantId = document.getElementById("bis-selected-variant-id")?.value;

            if (typeof BIS !== "undefined" && BIS.create) {
              BIS.create(email, variantId, productId).then((resp) => {

                if (resp.status === "OK") {
                  showBisSuccess();
                  return;
                } else {
                  showBisError();
                }
              });
            }
          })
          return true
        }
        
        // Try to attach immediately
        if (!attachBISListener()) {
          // If button not found, retry after a short delay (for custom CTA products)
          setTimeout(() => {
            attachBISListener()
          }, 100)
        }
      }

      initPageLoadState() {
        const productInfo = this.closest("product-info")
        if (!productInfo) return

        const sizeInputs = productInfo.querySelectorAll('.product--variant-size input[type="radio"]')
        const inseamInputs = productInfo.querySelectorAll('.product--variant-inseam input[type="radio"]')
        const placementRadios = productInfo.querySelectorAll('.product--variant-embroidery-placement input[type="radio"]')
        const petRadios = productInfo.querySelectorAll('.product--variant-number-of-pets input[type="radio"]')

        const sizeLabel = productInfo.querySelector(".product--variant-size [data-selected-value]")
        const inseamLabel = productInfo.querySelector(".product--variant-inseam [data-selected-value]")
        const placementLabel = productInfo.querySelector(".product--variant-embroidery-placement [data-selected-value]")
        const petLabel = productInfo.querySelector(".product--variant-number-of-pets [data-selected-value]")

        const hasSize = sizeInputs.length > 0
        const hasInseam = inseamInputs.length > 0
        const hasPlacement = placementRadios.length > 0
        const hasPets = petRadios.length > 0

        const backInStockWrapper = document.querySelector(".back-in-stock_wrapper.BIS_wrapper")

        const initRadios = (radios, label) => {
          radios.forEach((r) => { r.checked = false })
          if (label) label.textContent = ""
        }
        initRadios(sizeInputs, sizeLabel)
        initRadios(inseamInputs, inseamLabel)
        initRadios(placementRadios, placementLabel)
        initRadios(petRadios, petLabel)

        const isOOS = computeOOSState(
          productInfo.getSelectedVariant?.(productInfo),
          productInfo
        );

        if (isOOS) {
          this.submitButtonText.textContent = "Join the Waitlist";
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute("disabled"); // <-- Add this
          this.submitButton.classList.remove("disabled");
        } else {
          this.submitButtonText.textContent =
            document.body.classList.contains("template--product") ? "Add to Bag" : (window.variantStrings?.addToCart || "Add to Cart");
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute("disabled"); // <-- Add this
          this.submitButton.classList.remove("disabled");
        }

        if (backInStockWrapper) {
          backInStockWrapper.style.display = isOOS ? "block" : "none";
        }

        const allSizeInputs = [...productInfo.querySelectorAll('.product--variant-size input[type="radio"]')]
        const allSizesDisabled = allSizeInputs.length > 0 && allSizeInputs.every((input) => input.classList.contains("disabled"))
        if (!allSizesDisabled) {
          inseamInputs.forEach((radio) => {
            radio.classList.remove("disabled")
            radio.disabled = false
          })
        }

        requestAnimationFrame(() => {
          productInfo.removeAttribute("data-js-loading")
        })
      }

      async onSubmitHandler(evt) {
        evt.preventDefault()
        if (this.submitButton.getAttribute("aria-disabled") === "true") return

        // If variant is currently updating, queue the submission and wait
        const productInfo = this.closest("product-info")
        if (productInfo && productInfo.isUpdatingVariant) {
          // Wait for variant update to complete before proceeding
          await new Promise((resolve) => {
            const checkInterval = setInterval(() => {
              if (!productInfo.isUpdatingVariant) {
                clearInterval(checkInterval)
                resolve()
              }
            }, 50) // Check every 50ms

            // Timeout after 3 seconds to prevent infinite waiting
            setTimeout(() => {
              clearInterval(checkInterval)
              resolve()
            }, 3000)
          })
        }

        // Once the variant is settled, send out-of-stock selections to the BIS modal
        const isOOS = computeOOSState(
          productInfo?.getSelectedVariant?.(productInfo),
          productInfo
        );

        if (isOOS) {
          openBisModal(evt, false);
          return;
        }

        // Validate that required options are selected before adding to cart
        
        // Find the custom dropdown trigger or wrapper in custom-ui-select containers.
        let sizeTrigger = null
        let inseamTrigger = null

        const allCustomSelects = productInfo?.querySelectorAll(".custom-ui-select")
        allCustomSelects?.forEach((customSelect) => {
          const select = customSelect.querySelector("select")
          const wrapper = customSelect.querySelector("dropdown-input")
          const trigger =
            wrapper ||
            customSelect.querySelector(".ui-trigger") ||
            customSelect.querySelector(".dropdown__button") ||
            customSelect.querySelector(".dropdown__summary")

          if (select && trigger) {
            const nameAttr = select.name || ""
            if (nameAttr.toLowerCase().includes("size") && !nameAttr.toLowerCase().includes("inseam")) {
              sizeTrigger = trigger
            } else if (nameAttr.toLowerCase().includes("inseam")) {
              inseamTrigger = trigger
            }
          }
        })
        
        const sizeOK = isSizeOK()
        const inseamOK = isInseamOK()
        
        if (!sizeOK || !inseamOK) {
          if (sizeTrigger) {
            const btnSpan = sizeTrigger.querySelector && sizeTrigger.querySelector('.dropdown__button [data-label], .dropdown__button span')
            if (btnSpan) btnSpan.classList.toggle('has-error', !sizeOK)
            if (sizeTrigger.tagName !== "DROPDOWN-INPUT") {
              sizeTrigger.classList.toggle("error", !sizeOK)
            }
          }
          if (inseamTrigger) {
            const btnSpan = inseamTrigger.querySelector && inseamTrigger.querySelector('.dropdown__button [data-label], .dropdown__button span')
            if (btnSpan) btnSpan.classList.toggle('has-error', !inseamOK)
            if (inseamTrigger.tagName !== "DROPDOWN-INPUT") {
              inseamTrigger.classList.toggle("error", !inseamOK)
            }
          }
          const hasTriggerError = sizeTrigger || inseamTrigger
          if (hasTriggerError) {
            this.handleErrorMessage()
          } else {
            this.handleErrorMessage(false)
          }
          this.error = true

          const scrollTarget = sizeTrigger?.closest(".product-form__input, .custom-ui-select") ||
            inseamTrigger?.closest(".product-form__input, .custom-ui-select")
          if (scrollTarget) {
            scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" })
          }
          return
        }
        
        // Remove error class if validation passes
        if (sizeTrigger) {
          if (sizeTrigger.tagName !== "DROPDOWN-INPUT") sizeTrigger.classList.remove("error")
          const btnSpan = sizeTrigger.querySelector && sizeTrigger.querySelector('.dropdown__button [data-label], .dropdown__button span')
          if (btnSpan) btnSpan.classList.remove('has-error')
        }
        if (inseamTrigger) {
          if (inseamTrigger.tagName !== "DROPDOWN-INPUT") inseamTrigger.classList.remove("error")
          const btnSpan = inseamTrigger.querySelector && inseamTrigger.querySelector('.dropdown__button [data-label], .dropdown__button span')
          if (btnSpan) btnSpan.classList.remove('has-error')
        }

        this.handleErrorMessage()

        this.submitButton.setAttribute("aria-disabled", true)
        this.submitButton.classList.add("loading")
        this.querySelector(".loading__spinner").classList.remove("hidden")

        const config = fetchConfig("javascript")
        config.headers["X-Requested-With"] = "XMLHttpRequest"
        delete config.headers["Content-Type"]

        const formData = new FormData(this.form)
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.section)
          )
          formData.append("sections_url", window.location.pathname)
          this.cart.setActiveElement(document.activeElement)
        }
        config.body = formData

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              })
              this.handleErrorMessage(response.description)

              if (soldOutMessage) {
                soldOutMessage.classList.add("hidden");
              }
              this.submitButtonText.classList.remove("hidden");
              this.error = true
              return
            } else if (!this.cart) {
              window.location = window.routes.cart_url
              return
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              })
            this.error = false
            this.cart.renderContents(response)
          })
          .catch((e) => {
            console.error(e)
          })
          .finally(() => {
            this.submitButton.classList.remove("loading")
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty")
            if (!this.error) this.submitButton.removeAttribute("aria-disabled")
            this.querySelector(".loading__spinner").classList.add("hidden")
          })
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper")
        if (!this.errorMessageWrapper) return
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form__error-message")

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage)

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage
        }
      }

      toggleSubmitButton(disable = true) {
        const productInfo = this.closest("product-info");
        const isOOS = computeOOSState(
          productInfo?.getSelectedVariant?.(productInfo),
          productInfo
        );

        this.updateSubmitButtonState(isOOS);
      }

      updateSubmitButtonState(isOOS) {
        if (isOOS) {
          this.submitButtonText.textContent = "Join the Waitlist";
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute("disabled");
          this.submitButton.classList.remove("disabled");
        } else {
          this.submitButtonText.textContent =
            document.body.classList.contains("template--product") ? "Add to Bag" : (window.variantStrings?.addToCart || "Add to Cart");
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute("disabled");
          this.submitButton.classList.remove("disabled");
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]")
      }
    }
  )
}

document.addEventListener("DOMContentLoaded", () => {
  initBisModal();
});
document.addEventListener("click", (event) => {
  if (
    event.target.closest(".bis-modal__close") ||
    event.target.classList.contains("bis-modal__overlay")
  ) {
    closeBisModal();
  }
});

function positionSizeGuide() {
  const sizeGuideWrapper = document.querySelector('.size_inventory_wrapper');

  if (!sizeGuideWrapper) return;

  const sizeVariant = document.querySelector('.product--variant-size');

  if (sizeVariant) {
    const sizeLabelRow = sizeVariant.querySelector('.size-label-row');

    if (sizeLabelRow) {
      sizeLabelRow.appendChild(sizeGuideWrapper);
      sizeGuideWrapper.classList.remove('size-guide--no-variant');
      sizeGuideWrapper.classList.add('is-positioned');
    }
  } else {
    const buyButtons = document.querySelector('.product__buy-buttons');

    if (buyButtons) {
      buyButtons.parentNode.insertBefore(sizeGuideWrapper, buyButtons);
      sizeGuideWrapper.classList.add('size-guide--no-variant');
      sizeGuideWrapper.classList.add('is-positioned');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  positionSizeGuide();
});
